import type { User } from '../types/auth';

/** Free signup intro applies only while the account is still on daily limits — not monthly/unlimited Sulit/Boss. */
function isOnSignupIntroDailyTier(user: User): boolean {
  if (user.is_system_guest || user.is_superuser) return false;
  const mode = (user.compute_limit_mode || 'daily').toLowerCase().trim();
  return mode === 'daily';
}

/** After this instant (UTC), signed-in customers should see upgrade CTA (guest/superuser exempt). */
export function requiresPlanPurchase(user: User): boolean {
  if (user.is_system_guest || user.is_superuser) return false;
  if (!isOnSignupIntroDailyTier(user)) return false;
  if (!user.trial_ends_at) return false;
  const end = new Date(user.trial_ends_at).getTime();
  if (Number.isNaN(end)) return false;
  return Date.now() >= end;
}

export function introTrialDaysLeft(user: User): number | null {
  if (!isOnSignupIntroDailyTier(user) || !user.trial_ends_at) return null;
  const end = new Date(user.trial_ends_at).getTime();
  if (Number.isNaN(end)) return null;
  const msLeft = end - Date.now();
  if (msLeft <= 0) return null;
  return Math.max(1, Math.ceil(msLeft / 86_400_000));
}

/** Calendar length of intro (signup → trial end), whole days, for copy like "31-day intro". */
export function introTrialSpanDays(user: User): number | null {
  if (!isOnSignupIntroDailyTier(user) || !user.trial_ends_at || !user.created_at) return null;
  const start = new Date(user.created_at).getTime();
  const end = new Date(user.trial_ends_at).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  return Math.max(1, Math.ceil((end - start) / 86_400_000));
}

/** e.g. "Jun 7" or "Jun 7, 2027" in UTC when the reset date is not the current calendar year. */
export function introTrialResetOnUtcLabel(user: User): string | null {
  if (!isOnSignupIntroDailyTier(user) || !user.trial_ends_at) return null;
  const d = new Date(user.trial_ends_at);
  if (Number.isNaN(d.getTime())) return null;
  const endYear = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric' }).format(d);
  const nowYear = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric' }).format(new Date());
  const opts: Intl.DateTimeFormatOptions =
    endYear === nowYear
      ? { month: 'short', day: 'numeric', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  return new Intl.DateTimeFormat('en-US', opts).format(d);
}
